import { createFileRoute, notFound } from '@tanstack/react-router';
import Maximize2 from 'lucide-react/dist/esm/icons/maximize-2';
import Minimize2 from 'lucide-react/dist/esm/icons/minimize-2';
import { useEffect, useState } from 'react';
import Chart from '@/components/item/chart';
import PriceScale from '@/components/item/price-scale';
import Stats from '@/components/item/stats';
import { api, fetchApi } from '@/lib/api';
import { computeRichStats, mapPricesForChart, type RawPriceEntry } from '@/lib/item-stats';
import { ssrForPublicOnly } from '@/lib/ssr-utils';
import { EmbedError } from './-components/embed-error';
import { EmbedSkeleton } from './-components/embed-skeleton';
import { embedCspMiddleware } from './-lib/embed-csp-middleware';
import { isAllowedParentOrigin } from './-lib/use-extension-auth';

export const Route = createFileRoute('/embed/$uin')({
  ssr: ssrForPublicOnly,
  server: {
    middleware: [embedCspMiddleware],
  },
  pendingComponent: EmbedSkeleton,
  errorComponent: EmbedError,
  loader: async ({ params }) => {
    const { uin } = params;
    const uinNumber = Number(uin);
    if (Number.isNaN(uinNumber)) {
      throw notFound();
    }
    const item = await fetchApi(api.api.items({ uin: uinNumber }).get());
    const rawPrices = item.prices as RawPriceEntry[];
    const richStats = computeRichStats(
      rawPrices.filter((p): p is RawPriceEntry & { price: number } => (p.price ?? 0) > 0),
    );
    return {
      item,
      richStats,
    };
  },
  head: ({ loaderData }) => {
    const name = loaderData?.item?.name ?? 'PriceDrop';
    return {
      meta: [{ title: `${name} - PriceDrop` }, { name: 'robots', content: 'noindex, nofollow' }],
    };
  },
  component: EmbedPage,
});

function EmbedPage() {
  const { item, richStats } = Route.useLoaderData();
  const [statsVisible, setStatsVisible] = useState(true);

  const prices = mapPricesForChart(item.prices as RawPriceEntry[]);

  useEffect(() => {
    const widget = document.getElementById('widget');
    if (!widget) return;
    const params = new URLSearchParams(window.location.search);
    const iframeId = params.get('iframeId');
    // Prefer an explicit `parentOrigin` param (same convention as the
    // notification-modal route). Fall back to '*' only when it isn't
    // provided — this preserves backwards compatibility with older extension
    // builds that didn't set the param, while still tightening the target
    // origin for the current build.
    const parentOriginParam = params.get('parentOrigin');
    const resolvedTargetOrigin =
      parentOriginParam && isAllowedParentOrigin(parentOriginParam) ? parentOriginParam : '*';
    let lastHeight = 0;
    let debounceTimer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const height = widget.offsetHeight;
        if (Math.abs(height - lastHeight) >= 2) {
          lastHeight = height;
          // Receiving side validates event.origin + event.source + iframeId
          // in the registry, so '*' is not a trust boundary — but posting to
          // the known parent origin when available still reduces data spill
          // to siblings in the frame tree.
          window.parent.postMessage({ type: 'resize', height, iframeId }, resolvedTargetOrigin);
        }
      }, 100);
    });
    observer.observe(widget);
    return () => {
      observer.disconnect();
      clearTimeout(debounceTimer);
    };
  }, []);

  if (!prices.some((p) => p.price != null)) {
    return (
      <div id="widget" className="w-full bg-white px-2 py-1">
        <p className="py-8 text-center text-sm text-gray-500">אין נתוני מחיר זמינים</p>
      </div>
    );
  }

  return (
    <div id="widget" className="w-full bg-white px-2 py-1">
      <div className="relative mb-1 flex items-center justify-center">
        <a
          href={`https://pricedrop.co.il/item/${item.uin}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-base font-bold leading-none text-blue-600"
        >
          Pricedrop
        </a>
        <button
          type="button"
          onClick={() => setStatsVisible((v) => !v)}
          className="absolute start-0 hidden lg:flex items-center justify-center rounded p-1 text-gray-400 transition-colors hover:text-gray-600"
          aria-label={statsVisible ? 'הרחב גרף' : 'הצג סטטיסטיקות'}
        >
          {statsVisible ? (
            <Maximize2 className="h-3.5 w-3.5" />
          ) : (
            <Minimize2 className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      <div className="flex flex-col gap-2 lg:flex-row">
        <div className="h-[300px] min-w-0 flex-1 lg:h-auto lg:min-h-[300px]">
          <Chart
            prices={prices}
            stats={{
              max: richStats.max,
              min: richStats.min,
              meanPrice: item.stats.meanPrice,
              current: richStats.current,
            }}
            showRangeSelection={false}
            maintainAspectRatio={false}
          />
        </div>
        {/* Stats panel: width + opacity animate over 300ms; max-height snaps to 0
            after a 300ms delay so it only collapses once the slide-out is done.
            Both states use explicit px values for max-height so the browser can
            interpolate (none→0 is not transitionable and would snap immediately).
            The inner div keeps a fixed 320px width so text never wraps at 0-width. */}
        <div
          className="hidden shrink-0 overflow-hidden lg:block"
          style={
            statsVisible
              ? {
                  width: '320px',
                  maxHeight: '9999px',
                  opacity: 1,
                  transition: 'width 300ms ease-in-out, opacity 300ms ease-in-out',
                }
              : {
                  width: 0,
                  maxHeight: 0,
                  opacity: 0,
                  pointerEvents: 'none',
                  transition:
                    'width 300ms ease-in-out, opacity 300ms ease-in-out, max-height 0ms linear 300ms',
                }
          }
        >
          <div className="w-[320px]">
            <Stats stats={item.stats} />
          </div>
        </div>
      </div>

      {richStats.max.price !== richStats.min.price && (
        <div className="my-2">
          <PriceScale
            meanPrice={item.stats.meanPrice}
            current={richStats.current}
            min={richStats.min}
            max={richStats.max}
          />
        </div>
      )}
    </div>
  );
}
